import React, { useEffect, useState } from 'react';
import {
  Activity,
  BadgeCheck,
  Brain,
  ChevronDown,
  Clock3,
  FileText,
  Hash,
  Layers3,
  Loader2,
  Puzzle,
  Sparkles,
  Type,
} from 'lucide-react';
import { formatSearchNumber, getResultCountDisplay } from '../../utils/searchDisplay';

const QUERY_TYPE_LABELS = {
  code_exact: 'код',
  code_prefix: 'код по началу',
  article: 'артикул',
  brand: 'марка/бренд',
  keyword: 'слово',
  phrase: 'описание',
  semantic: 'похожие товары',
  ilike_description: 'подстрока',
};

const METHOD_META = {
  exact_code:        { label: 'Код точно',         title: 'Точное совпадение кода товара',                            Icon: Hash,       tone: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/50' },
  prefix_code:       { label: 'Код по началу',     title: 'Поиск по началу кода товара',                              Icon: Hash,       tone: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-900/50' },
  fulltext:          { label: 'Слова',             title: 'Совпадение слов в описании',                               Icon: FileText,   tone: 'bg-sky-50 text-sky-700 border-sky-200 dark:bg-sky-950/30 dark:text-sky-300 dark:border-sky-900/50' },
  semantic:          { label: 'Похожие',           title: 'Похожие по смыслу описания',                               Icon: Sparkles,   tone: 'bg-violet-50 text-violet-700 border-violet-200 dark:bg-violet-950/30 dark:text-violet-300 dark:border-violet-900/50' },
  trigram:           { label: 'Похожий артикул',   title: 'Нечеткое совпадение по артикулу или модели в описании',    Icon: Puzzle,     tone: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-900/50' },
  trademark_trigram: { label: 'Марка похожая',     title: 'Нечеткое совпадение по торговой марке',                    Icon: BadgeCheck, tone: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-900/50' },
  trademark_ilike:   { label: 'Марка точная',      title: 'Точное текстовое совпадение по торговой марке',            Icon: BadgeCheck, tone: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/30 dark:text-teal-300 dark:border-teal-900/50' },
  trademark:         { label: 'Марка',             title: 'Совпадение по торговой марке',                             Icon: BadgeCheck, tone: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-900/50' },
  importer_trigram:  { label: 'Импортёр похожий',  title: 'Нечеткое совпадение по импортёру',                         Icon: BadgeCheck, tone: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/30 dark:text-teal-300 dark:border-teal-900/50' },
  importer:          { label: 'Импортёр',          title: 'Совпадение по импортёру',                                  Icon: BadgeCheck, tone: 'bg-teal-50 text-teal-700 border-teal-200 dark:bg-teal-950/30 dark:text-teal-300 dark:border-teal-900/50' },
  ilike_description: { label: 'Подстрока',         title: 'Буквальная подстрока в описании',                          Icon: Type,       tone: 'bg-gray-50 text-gray-700 border-gray-200 dark:bg-slate-700/40 dark:text-slate-200 dark:border-slate-600' },
};

function MethodBadge({ method }) {
  const meta = METHOD_META[method] || { label: method, title: method, Icon: Activity, tone: 'bg-app text-text-muted border-border-default' };
  const Icon = meta.Icon;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 h-[22px] rounded-md border text-[11px] font-medium ${meta.tone}`}
      title={meta.title}
    >
      <Icon className="w-3 h-3" />
      {meta.label}
    </span>
  );
}

export default function SearchDiagnostics({ searchInfo, totalEstimate, loading }) {
  const [expanded, setExpanded] = useState(() => {
    try { return localStorage.getItem('customs-search:diag-expanded') === 'true'; }
    catch { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem('customs-search:diag-expanded', expanded ? 'true' : 'false'); }
    catch { /* ignore */ }
  }, [expanded]);

  if (loading) {
    return (
      <div className="flex-shrink-0 flex items-center gap-2 border-b border-border-default bg-surface px-4 py-1.5 text-xs text-text-muted">
        <Loader2 className="w-3.5 h-3.5 animate-spin text-blue-600 dark:text-blue-400" />
        Ищем по 10,27 М деклараций…
      </div>
    );
  }

  if (!searchInfo) return null;

  const methods = searchInfo.methods_used || [];
  const queryType = QUERY_TYPE_LABELS[searchInfo.query_type] || searchInfo.query_type || 'auto';
  const subQueries = searchInfo.sub_queries || [];
  const countDisplay = getResultCountDisplay(totalEstimate, searchInfo);
  const duplicateSuppressed = Number(searchInfo.duplicate_suppressed || 0);
  const reranked = Boolean(searchInfo.reranked);
  const ms = Number(searchInfo.execution_time_ms || 0);

  const hasSignal = reranked || duplicateSuppressed > 50 || subQueries.length > 0;

  return (
    <div className="flex-shrink-0 bg-surface">
      {/* Compact row */}
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 border-b border-border-default px-4 py-1.5 text-xs">
        <span className="inline-flex items-center gap-1.5" title={countDisplay.title}>
          <span className="text-text-muted">{countDisplay.prefix}</span>
          <span className="font-semibold text-text-primary tabular-nums">{countDisplay.value}</span>
          <span className="text-text-muted">{countDisplay.suffix}</span>
        </span>
        <span className="text-text-disabled">·</span>
        <span className="inline-flex items-center gap-1 text-text-muted tabular-nums">
          <Clock3 className="w-3 h-3" />
          {(ms / 1000).toFixed(1)} с
        </span>
        <span className="text-text-disabled">·</span>
        <span className="text-text-muted">
          тип: <span className="text-text-secondary">{queryType}</span>
        </span>

        <button
          type="button"
          onClick={() => setExpanded(e => !e)}
          className="ml-auto inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-[11px] font-medium text-text-muted hover:bg-hover hover:text-text-secondary transition-colors"
        >
          {hasSignal && !expanded && (
            <span className="h-1.5 w-1.5 rounded-full bg-violet-500 dark:bg-violet-400" title="Есть нетривиальные сигналы поиска" />
          )}
          {expanded ? 'Скрыть детали' : 'Подробнее о поиске'}
          <ChevronDown className={`w-3 h-3 transition-transform ${expanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Expanded grid */}
      {expanded && (
        <div className="grid grid-cols-12 gap-x-6 gap-y-3 border-b border-border-default bg-app/60 px-4 py-3 text-xs">
          <div className="col-span-12 md:col-span-4">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Методы поиска</div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {methods.length > 0
                ? methods.map(m => <MethodBadge key={m} method={m} />)
                : <span className="text-text-disabled">—</span>}
            </div>
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Альтернативные запросы</div>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {subQueries.length > 0
                ? subQueries.map((q, i) => (
                    <span
                      key={`${q}-${i}`}
                      className="inline-flex h-[22px] items-center rounded-md border border-border-default bg-surface px-2 text-[11px] text-text-secondary max-w-[260px] truncate"
                      title={q}
                    >
                      {q}
                    </span>
                  ))
                : <span className="text-text-disabled">—</span>}
            </div>
          </div>

          <div className="col-span-12 md:col-span-3">
            <div className="text-[10px] uppercase tracking-wider text-text-muted">Доп. обработка</div>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {reranked && (
                <span
                  className="inline-flex items-center gap-1 px-2 h-[22px] rounded-md border border-violet-200 bg-violet-50 text-[11px] font-medium text-violet-700 dark:border-violet-900/50 dark:bg-violet-950/30 dark:text-violet-300"
                  title="AI пересортировал верхнюю часть выдачи"
                >
                  <Brain className="w-3 h-3" />
                  AI-сортировка
                </span>
              )}
              {duplicateSuppressed > 0 && (
                <span
                  className="inline-flex items-center gap-1 px-2 h-[22px] rounded-md border border-slate-200 bg-slate-50 text-[11px] font-medium text-slate-700 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-200"
                  title="Похожие строки в хвосте выдачи скрыты, чтобы первый экран был разнообразнее"
                >
                  <Layers3 className="w-3 h-3" />
                  −{formatSearchNumber(duplicateSuppressed)} повторов
                </span>
              )}
              {!reranked && duplicateSuppressed === 0 && (
                <span className="text-text-disabled">—</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
